import { useMemo } from 'react';
import { CircleAlert, EyeOff, X } from 'lucide-react';
import { Avatar, IconButton, Spinner, UserBadge } from '../../components/ui';
import { Markdown } from '../../lib/markdown';
import { formatTime } from '../../lib/time';
import { useStore } from '../../store/store';
import { useRenderContext } from './MessageItem';
import { ModalForms } from './ModalForms';
import { RichEmbedView } from './RichEmbed';
import { dismissInteraction, useCommands } from './slashCommands';

/**
 * Slash commands I ran in this channel that are still showing: the bot thinking, a failure, or an answer
 * only I can see. A normal answer is a message in the channel, so it replaces the notice.
 */
export function InteractionNotices({ channelId, spaceId }: { channelId: string; spaceId: string | null }) {
  const all = useCommands((s) => s.interactions);
  const interactions = useMemo(() => Object.values(all).filter((i) => i.channelId === channelId), [all, channelId]);
  const users = useStore((s) => s.users);
  const ctx = useRenderContext(spaceId);
  if (interactions.length === 0) return <ModalForms channelId={channelId} />;
  return (
    <div className="space-y-1.5 pb-1.5" aria-live="polite">
      <ModalForms channelId={channelId} />
      {interactions
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
        .map((i) => {
          const bot = users[i.botId];
          const name = bot?.displayName ?? 'The bot';
          return (
            <div key={i.id} className="flex items-start gap-3 rounded-lg border border-line bg-sidebar px-3 py-2">
              <Avatar user={bot} size={28} />
              <div className="min-w-0 flex-1 text-sm">
                <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted">
                  <span className="flex items-center font-semibold text-fg">
                    {name}
                    <UserBadge user={bot} />
                  </span>
                  <span>
                    {'·'}{' '}
                    {i.commandName && (
                      <>
                        <span className="font-medium text-accent">
                          {i.commandType === 'chat' || !i.commandType ? '/' : ''}
                          {i.commandName}
                        </span>{' '}
                        {'·'}{' '}
                      </>
                    )}
                    {formatTime(i.createdAt)}
                  </span>
                </div>
                {i.status === 'pending' || i.status === 'deferred' ? (
                  <div className="mt-0.5 flex items-center gap-2 text-muted">
                    <Spinner className="size-4" label="Waiting for the bot" />
                    {name} is thinking…
                  </div>
                ) : i.status === 'failed' ? (
                  <div className="mt-0.5 flex items-start gap-1.5 text-danger">
                    <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
                    <span>{i.error ?? 'The command failed.'}</span>
                  </div>
                ) : i.ephemeral ? (
                  <>
                    <div className="mt-0.5 whitespace-pre-wrap break-words text-fg">
                      <Markdown content={i.ephemeral.content} ctx={ctx} />
                    </div>
                    {i.ephemeral.embeds?.map((e, n) => (
                      <div key={n} className="mt-1.5">
                        <RichEmbedView e={e} ctx={ctx} />
                      </div>
                    ))}
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-faint">
                      <EyeOff className="size-3" aria-hidden /> Only you can see this
                    </div>
                  </>
                ) : null}
              </div>
              {i.status !== 'pending' && i.status !== 'deferred' && <IconButton icon={X} label="Dismiss" size="sm" onClick={() => dismissInteraction(i.id)} />}
            </div>
          );
        })}
    </div>
  );
}
